import {Trans} from '@lingui/macro';
import React, {PropsWithChildren} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {OkCancelDialog} from '../../../components/common/dialogs/okCancelDialog';
import {SETTINGS_FOOTER_HEIGHT} from '../../common/settingsWrapper';
import {RuleExpressionCardsWrapper} from '../../rules/ruleEditor/ruleExpressionCardsWrapper';
import {settingsHorizontalPaddingStyles} from '../../settingsCommonConstants';
import {useBaseEditorContext} from '../baseEditorContext';
import {useExpressionsContext} from './expressionsContext';
import {ExpressionsSidePanelAtomsList} from './expressionsSidePanelAtomsList';
import {ExpressionsSidePanelCreate} from './expressionsSidePanelCreate';
import {ExpressionsSidePanelHeader} from './expressionsSidePanelHeader';
import {ExpressionDependencies, ExpressionState, ExpressionStateActions} from './expressionState';

/*
 * Const.
 */

const PANEL_WIDTH = 550;

const TRANSITION_PERIOD = '0.2s';
const TRANSITION_TIMING_FUNCTION = 'ease-in-out';

/*
 * Props.
 */

type WithExecDryRun = {onExecDryRun?: Handler};

interface ExpressionsSidePanelProps<
  S extends ExpressionState,
  A extends ExpressionStateActions & WithExecDryRun,
> {
  state: S;
  actions: A;
  expressionsDependencies: ExpressionDependencies;
}

/*
 * Style.
 */

const StyledExpressionsSidePanelAside = styled.aside<{$isOpen: boolean}>`
  position: absolute;
  overflow: hidden;
  right: 0;
  top: 0;

  display: grid;
  grid-template-rows: 58px 1fr ${SETTINGS_FOOTER_HEIGHT}px;

  width: ${`${PANEL_WIDTH}px`};
  height: 100%;

  background-color: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0px 1px 9px ${p.theme.alphas.black20}`)};

  visibility: ${(p) => (p.$isOpen ? 'visible' : 'hidden')};
  transform: translateX(${(p) => (p.$isOpen ? 0 : `${PANEL_WIDTH}px`)});
  transition: transform ${TRANSITION_PERIOD} ${TRANSITION_TIMING_FUNCTION}, visibility ${TRANSITION_PERIOD};
`;

// @TODO: Change RuleExpressionCardsWrapper.
const StyledExpressionsSidePanelContentWrapper = styled(RuleExpressionCardsWrapper)`
  padding: 16px;
  height: 100%;
  overflow-y: auto;
`;

const StyledExpressionsSidePanelFooter = styled.footer`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};

  ${settingsHorizontalPaddingStyles};

  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 8px;
  align-items: center;

  height: ${SETTINGS_FOOTER_HEIGHT}px;
`;

const StyledLeftButton = styled(Button)`
  margin-right: auto;
`;

/*
 * Component.
 */

// @TODO: Change delete confirmation dialog text (should not refer to "rule conditions and actions").

export const ExpressionsSidePanel = <
  S extends ExpressionState,
  A extends ExpressionStateActions & WithExecDryRun,
>(
  props: PropsWithChildren<ExpressionsSidePanelProps<S, A>>,
) => {
  const {state, actions, expressionsDependencies} = props;
  const {isOpen, selectedExpression} = state.expressionPanelState;

  const {isReadOnly} = useBaseEditorContext();
  const {expressions, expressionCreationState, setExpressionCreationState} = useExpressionsContext();

  const [isCloseConfirmationOpen, toggleCloseConfirmation] = useToggle();

  const shouldDisplayDryRun = Boolean(
    actions.onExecDryRun &&
      selectedExpression &&
      selectedExpression.atoms.length > 0 &&
      state.hasDryRunTestData,
  );

  const isEditing = Boolean(
    selectedExpression && expressions.some((exp) => exp.id === selectedExpression.id),
  );

  const onExpressionSave = () => {
    if (!selectedExpression) {
      return;
    }

    // @TODO: Tracking.

    actions.onSaveDraftExpression();

    expressionCreationState?.onCreateExpression(selectedExpression);
    setExpressionCreationState(undefined);
  };

  return (
    <StyledExpressionsSidePanelAside
      $isOpen={isOpen}
      {...buildInteractionIdProps('editor-expression-side-panel')}
    >
      <ExpressionsSidePanelHeader
        onClose={() => actions.onSetPanelOpen(false)}
        isEditing={isEditing}
        onDelete={isEditing ? toggleCloseConfirmation : undefined}
      />
      <StyledExpressionsSidePanelContentWrapper>
        {selectedExpression && selectedExpression.atoms.length > 0 ? (
          <ExpressionsSidePanelAtomsList
            expressionDependencies={expressionsDependencies}
            isReadonly={isReadOnly}
            expression={selectedExpression}
            actions={actions}
          />
        ) : (
          <ExpressionsSidePanelCreate
            onAddExpression={(atoms) => actions.onAddExpression(atoms)}
            expressionsDependencies={expressionsDependencies}
          />
        )}
      </StyledExpressionsSidePanelContentWrapper>
      <StyledExpressionsSidePanelFooter>
        {shouldDisplayDryRun && (
          <StyledLeftButton onClick={actions.onExecDryRun}>
            <Trans id="rule-editor-side-panel-dryrun">Dry run</Trans>
          </StyledLeftButton>
        )}

        <Button onClick={() => actions.onSetPanelOpen(false)}>
          <Trans id="editor-expression-side-panel-cancel">Cancel</Trans>
        </Button>
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={!selectedExpression}
          onClick={onExpressionSave}
        >
          <Trans id="editor-expression-side-panel-save">Save</Trans>
        </Button>
      </StyledExpressionsSidePanelFooter>
      {isCloseConfirmationOpen && selectedExpression && (
        <OkCancelDialog
          title={
            <Trans id="editor-expression-delete-dialog-title">
              Delete {selectedExpression.name} variable
            </Trans>
          }
          okButtonLabel={<Trans id="editor-expression-delete-dialog-confirm">Delete</Trans>}
          onOkClick={() => {
            actions.onDeleteExpression(selectedExpression.id);
            toggleCloseConfirmation();
          }}
          onRequestClose={toggleCloseConfirmation}
          okButtonVariant={ButtonVariantsEnum.DANGER}
        >
          <Trans id="editor-expression-delete-dialog-text">
            Deleting this dynamic variable will remove it from your rule conditions and actions. It will also
            invalidate other variables referencing it.
          </Trans>
        </OkCancelDialog>
      )}
    </StyledExpressionsSidePanelAside>
  );
};
